import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLatestRef } from '../hooks/useLatestRef'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { isExtensionInstalled, getExtensionVersion, isExtensionVersionSupported, isExtensionUpdateAvailable, MIN_EXTENSION_VERSION, LATEST_EXTENSION_VERSION, sendCommand } from '../lib/extension'
import {
  buildComposeRoute,
  buildFilterRoute,
  buildInboxRoute,
  buildInvitesRoute,
  buildSettingsRoute,
  buildTemplatesRoute,
  parseRoute,
} from '../lib/routes'
import { db, type Conversation } from '../lib/db'
import { useCategoryAssignments } from '../hooks/useCategories'
import { useBookmarks } from '../hooks/useBookmarks'
import { useExtensionHealth } from '../hooks/useExtensionHealth'
import { useSearch } from '../hooks/useSearch'
import { useDrafts } from '../hooks/useDrafts'
import { useShortcuts, isEditableElement } from '../hooks/useShortcuts'
import Sidebar from '../components/Sidebar'
import ConversationList from '../components/ConversationList'
import ThreadView from '../components/ThreadView'
import ComposeView from '../components/ComposeView'
import InviteList from '../components/InviteList'
import InviteDetail from '../components/InviteDetail'
import ConnectionsList from '../components/ConnectionsList'
import OrganizeModal from '../components/OrganizeModal'
import CommandPalette from '../components/CommandPalette'
import PendingInvitations from '../components/PendingInvitations'
import ShortcutsHelpPanel from '../components/ShortcutsHelpPanel'
import { ActionTooltip, Kbd } from '../components/Kbd'
import SelectionActionBar from '../components/SelectionActionBar'
import { ErrorBoundary } from '../components/ErrorBoundary'
import Templates from './Templates'
import Settings from './Settings'
import { voidAsync } from '../lib/voidAsync'
import { getCachedUserId, readPreferencesCache } from '../lib/sessionCache'
import { useInvites } from '../hooks/useInvites'
import { categorizeByHeadline, PERSON_CATEGORIES } from '../lib/categorize'
import { useConnections, type Connection } from '../hooks/useConnections'
import { useReminders } from '../hooks/useReminders'
import { useTags, useTagAssignments } from '../hooks/useTags'
import { useComposites } from '../hooks/useComposites'
import { evaluateComposite } from '../lib/compositeEvaluator'
import { useSalesNavPoller } from '../hooks/useSalesNavPoller'
import { useInviteSync } from '../hooks/useInviteSync'
import { useContactSync } from '../hooks/useContactSync'
import { useCrmAutoSync } from '../hooks/useCrmAutoSync'
import { useCrmSyncReconciler } from '../hooks/useCrmSyncReconciler'
import { useArchiveUndo } from '../hooks/useArchiveUndo'

// Inbox view modes (Kondo-style). ALL is the default — all non-archived
// Inbox conversations. The others are filters scoped to the Inbox list.
//   - WAITING_THEIRS: I sent the last message; waiting for them to reply.
//   - WAITING_MINE:   they sent the last message; waiting for me to reply.
//   - UNREAD:         !isRead (supersedes the old standalone UNREAD inbox).
export type InboxView = 'ALL' | 'WAITING_THEIRS' | 'WAITING_MINE' | 'UNREAD'

const INBOX_VIEW_ALIASES = new Set<string>(['WAITING_THEIRS', 'WAITING_MINE', 'UNREAD'])

// Order matters — menu renders in this sequence. Every view is always
// available; WAITING_* depends on `lastMessageFromMe` written on the
// Conversation row at sync time by the extension, so the filter works
// without any server-side `linkedin_profile_urn` plumbing.
// See docs/adr/0002-write-time-conversation-denormalization.md.
const INBOX_VIEW_ITEMS: readonly {
  key: InboxView
  label: string
  shortcut: string
}[] = [
  { key: 'ALL',             label: 'All excluding archived', shortcut: 'A' },
  { key: 'WAITING_THEIRS',  label: 'Waiting for their reply', shortcut: 'S' },
  { key: 'WAITING_MINE',    label: 'Waiting for my reply',    shortcut: 'W' },
  { key: 'UNREAD',          label: 'Unread',                  shortcut: 'Q' },
]

export default function Shell() {
  const { signOut } = useAuth()
  const { isStale, extensionUnresponsive, extensionInvalidated } = useExtensionHealth()
  // Subscribe to invite-event broadcasts from the extension — mirror to Dexie
  // + forward to Supabase. No render output, pure side-effect. Mounted here
  // (not per-thread) because events can fire from anywhere in the app.
  useInviteSync()
  useContactSync()
  useCrmAutoSync()
  useCrmSyncReconciler()
  const [extInstalled, setExtInstalled] = useState<boolean | null>(null)
  const [extVersion, setExtVersion] = useState<string | null>(null)
  const [hasSalesNav, setHasSalesNav] = useState(false)
  const [hasRecruiter, setHasRecruiter] = useState(false)
  // ─── URL-driven routing (ADR 0026) ─────────────────────────────────────
  //
  // activeFilter / inboxView / selectedUrn / activePage / composeMode /
  // selectedInviteId all come from the URL. The `setX` functions below are
  // navigate-call shims that keep the pre-router API at downstream call
  // sites (sidebar clicks, keyboard shortcuts, command palette). As a
  // result, deep-linking to any conversation / filter / settings section
  // Just Works — no UI path state is hidden in component memory.
  const location = useLocation()
  const navigate = useNavigate()
  const route = useMemo(() => parseRoute(location.pathname), [location.pathname])
  const activeFilter = route.activeFilter
  const inboxView = route.inboxView
  const selectedUrn = route.selectedUrn
  const selectedInviteId = route.selectedInviteId
  const composeMode = route.composeMode
  // activePage — derived: /compose renders the inbox-shell with the
  // compose overlay, so from the Shell's "which top-level pane am I?"
  // perspective, /compose is still 'inbox'.
  const activePage: 'inbox' | 'templates' | 'settings' =
    route.activePage === 'compose' ? 'inbox' : route.activePage

  // On mount at the root URL, redirect to the user's preferred default
  // view per their cached preferences. Covers the legacy UNREAD →
  // /inbox/unread migration path (preferences cache still stores
  // defaultView='UNREAD' from pre-kondo-parity days). Replace instead of
  // push so back-button doesn't bounce back to '/'.
  useEffect(() => {
    if (location.pathname !== '/') return
    try {
      const userId = getCachedUserId()
      const cached = userId ? readPreferencesCache(userId) as
        | { defaultView?: unknown; sidebarConfig?: Array<{ key?: unknown; visible?: unknown }> }
        | null : null
      if (!cached) { navigate(buildInboxRoute('ALL'), { replace: true }); return }

      const visibleKeys = Array.isArray(cached.sidebarConfig)
        ? cached.sidebarConfig
            .filter((s) => s && typeof s.key === 'string' && s.visible !== false)
            .map((s) => s.key as string)
        : []
      const candidate = typeof cached.defaultView === 'string' ? cached.defaultView : null

      if (candidate === 'UNREAD') {
        navigate(buildInboxRoute('UNREAD'), { replace: true })
        return
      }
      if (candidate && (visibleKeys.length === 0 || visibleKeys.includes(candidate))) {
        navigate(buildFilterRoute(candidate), { replace: true })
        return
      }
      if (visibleKeys.length > 0) {
        navigate(buildFilterRoute(visibleKeys[0]), { replace: true })
        return
      }
    } catch { /* fall through */ }
    navigate(buildInboxRoute('ALL'), { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // mount only — we don't want to fight user navigation later

  // Shim setters — preserve the useState-style API at call sites so the
  // sidebar / palette / keyboard-shortcut handlers don't all need to
  // know about navigate(). Identity is stable for `navigate` from
  // react-router; other deps reflect current URL-derived state so that
  // closures capture fresh values.
  //
  // NB: a generic `setActiveFilter(key)` shim was removed — all
  // downstream call sites go through `navigateToFilter()` (defined
  // below), which also knows how to route INBOX_VIEW_ALIASES to
  // `/inbox/:view`. Adding the generic shim back would resurrect a
  // footgun where `setActiveFilter('UNREAD')` silently breaks because
  // UNREAD isn't a real filter key — it's an inboxView alias.

  const setInboxView = useCallback((view: InboxView) => {
    navigate(buildInboxRoute(view))
  }, [navigate])

  const setSelectedUrn = useCallback((urn: string | null) => {
    // Preserve the current filter context when opening a conversation.
    // INBOX also preserves the view slug (e.g. /inbox/waiting-theirs/c/X).
    //
    // When `urn === null` (user escaped out of a thread), replace the
    // current history entry rather than pushing a new one. Otherwise
    // every escape creates a back-button target that takes the user
    // BACK to the thread they just closed — mismatched with Gmail-
    // style expectations and not intended in ADR 0026. Opening a new
    // thread (urn is truthy) still pushes so back-button walks between
    // threads works.
    const options = urn ? undefined : { replace: true }
    if (activeFilter === 'INBOX') {
      navigate(buildInboxRoute(inboxView, urn), options)
    } else {
      navigate(buildFilterRoute(activeFilter, urn), options)
    }
  }, [navigate, activeFilter, inboxView])

  const setActivePage = useCallback((page: 'inbox' | 'templates' | 'settings') => {
    if (page === 'templates') navigate(buildTemplatesRoute())
    else if (page === 'settings') navigate(buildSettingsRoute())
    else navigate(buildInboxRoute(inboxView))
  }, [navigate, inboxView])

  const setComposeMode = useCallback((on: boolean) => {
    if (on) navigate(buildComposeRoute())
    else navigate(buildInboxRoute(inboxView))
  }, [navigate, inboxView])

  const setSelectedInviteId = useCallback((id: string | null) => {
    navigate(buildInvitesRoute(id))
  }, [navigate])

  const [inboxViewMenuOpen, setInboxViewMenuOpen] = useState(false)
  // Position of the filter-menu popover, measured from the button on open.
  // The popover renders in a portal (see below) because the conversation
  // column has overflow-hidden ancestors that would otherwise clip the
  // 280px menu, and the list's absolutely-positioned virtualizer items
  // can muddy stacking. `right` is measured from the viewport's right
  // edge so the menu's right edge stays pinned to the button.
  const filterBtnRef = useRef<HTMLButtonElement>(null)
  const [filterMenuPos, setFilterMenuPos] = useState<{ top: number; right: number } | null>(null)
  const [composeRecipient, setComposeRecipient] = useState<Connection | null>(null)
  const [highlightedUrn, setHighlightedUrn] = useState<string | null>(null)
  const [organizeOpen, setOrganizeOpen] = useState(false)
  const [showReminderPicker, setShowReminderPicker] = useState(false)
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false)
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false)
  const invitesHook = useInvites()
  const connectionsHook = useConnections()
  const remindersHook = useReminders()
  const tagsHook = useTags()
  const tagAssignmentsHook = useTagAssignments()
  const compositesHook = useComposites()
  // Web-driven Sales Nav sync poller (kondo-parity 1.9 final piece —
  // ADR 0035). No-op when the user doesn't have Sales Nav; the
  // extension's own alarm-based sync remains active regardless.
  useSalesNavPoller({ hasSalesNav })
  const [showTagPicker, setShowTagPicker] = useState(false)
  // State exists to trigger re-renders; reads happen via activePaneRef so the
  // shortcut handler always sees the latest value without re-creating callbacks.
  const [, _setActivePane] = useState<'list' | 'thread'>('list')
  const activePaneRef = useRef<'list' | 'thread'>('list')
  const setActivePane = useCallback((p: 'list' | 'thread') => { _setActivePane(p); activePaneRef.current = p }, [])
  const [selectedUrns, setSelectedUrns] = useState<Set<string>>(new Set())
  const [batchTagUrns, setBatchTagUrns] = useState<Set<string> | null>(null)
  const lastClickedUrn = useRef<string | null>(null)
  const navListRef = useRef<Conversation[]>([])
  const threadCopyRef = useRef<(() => void) | null>(null)
  const gKeyPressed = useRef<number>(0)
  const isMultiSelect = selectedUrns.size > 0

  // Hold remindersHook in a ref so handleSelectConversation doesn't re-bind
  // every render. remindersHook is a fresh object on each useReminders()
  // return, which would invalidate handleSelectConversation's identity and
  // cascade into the shortcutHandler / selector memoisation below. Ref keeps
  // the callback stable while still reading the latest hook methods at call
  // time. useLayoutEffect so a click handler firing in the same commit
  // picks up the latest hook without a tick of lag, and so the ref write
  // isn't flagged as a render-phase mutation by react-hooks/refs.
  const remindersHookRef = useRef(remindersHook)
  useLayoutEffect(() => {
    remindersHookRef.current = remindersHook
  }, [remindersHook])

  // Side effects of opening a conversation: optimistic mark-as-read,
  // MARK_READ RPC, auto-clear due reminder, highlight + multi-select
  // clear. Does NOT navigate — callers do the navigate themselves so
  // call sites that need to simultaneously switch *filter* (e.g. opening
  // a SalesNav conv from the connections picker) can do so in one history
  // entry rather than stacking two.
  // NOTE ON ORDERING (review LOW finding):
  // This callback references `setAllConversations`, `allConversationsRef`,
  // and `remindersHookRef` — all declared *later* in this file. The code
  // works because (a) the useCallback body doesn't execute during render,
  // only when invoked; (b) by invocation time the component has finished
  // evaluating and all `const`/`useState`/`useRef` declarations have run.
  // The `[]` deps array is correct: setters and refs have stable identity,
  // and the hook dep linter agrees. The theoretical footgun is a future
  // caller invoking this synchronously during render (e.g. from a
  // useLayoutEffect misplaced above the state declarations); the
  // resulting ReferenceError would point right here. If that becomes a
  // real concern, move this `useCallback` + its caller
  // `handleSelectConversation` below line ~420 where the state is
  // declared.
  const selectConversationSideEffects = useCallback((urn: string) => {
    setHighlightedUrn(urn)
    setSelectedUrns(new Set())
    lastClickedUrn.current = urn
    // Optimistically mark read in local state
    setAllConversations((prev) =>
      prev.map((c) =>
        c.conversationUrn === urn && !c.isRead
          ? { ...c, isRead: true, unreadCount: 0 }
          : c
      )
    )
    // Fire API call in background — route to Sales Nav API if applicable
    const conv = allConversationsRef.current.find((c) => c.conversationUrn === urn)
    const isSalesNav = conv?.category === 'SALESNAV'
    sendCommand('MARK_READ', { conversationUrn: urn, salesNav: isSalesNav || undefined })
      .catch((err) => console.warn('[Shell] MARK_READ failed:', err?.message))
    // Auto-clear due reminders when opening the conversation
    const reminderTime = remindersHookRef.current.getReminderTime(urn)
    if (reminderTime && reminderTime <= Date.now()) {
      voidAsync(remindersHookRef.current.clearReminder(urn), 'clearReminder (auto)', { urn })
    }
  }, [])

  // Primary conversation-click handler. Navigates to the URL for the current
  // filter with the conv open, then fires side effects. Single navigate to
  // avoid stacking history entries.
  const handleSelectConversation = useCallback((urn: string) => {
    if (composeMode) {
      // Exiting compose via conv-click: safe default is /inbox + conv.
      // Preserving the previous filter across compose would require a
      // stash we don't keep.
      navigate(buildInboxRoute('ALL', urn))
    } else if (activeFilter === 'INBOX') {
      navigate(buildInboxRoute(inboxView, urn))
    } else {
      navigate(buildFilterRoute(activeFilter, urn))
    }
    selectConversationSideEffects(urn)
  }, [navigate, composeMode, activeFilter, inboxView, selectConversationSideEffects])
  const search = useSearch({ hasSalesNav })
  const { draftUrns, draftCount, getDraftText, removeDraftLocally } = useDrafts()


  // Wrap respondToInvite to also inject accepted invites into the connections list
  //
  // POST_FIX_ASTAR_AUDIT_HANDOFF finding #2: the earlier code used
  // `invite.entityUrn.split(':').pop()` which extracts the invitation
  // ID — NOT the profile ID — and fed it to `addConnection` as if it
  // were a profile URN. Local-only corruption (reconciled on next
  // connections fetch) but wrong all the same. Use `invite.profileUrn`
  // directly; skip optimistic insertion when it's null.
  const handleInviteRespond = useCallback((invite: import('../hooks/useInvites').Invite, action: 'accept' | 'ignore') => {
    const promise = invitesHook.respondToInvite(invite, action)
    if (action === 'accept' && invite.profileUrn) {
      connectionsHook.addConnection({
        name: invite.name,
        headline: invite.headline,
        profileUrn: invite.profileUrn,
        profileImage: invite.profileImage,
        group: 'Today',
      })
    }
    return promise
  }, [invitesHook, connectionsHook])

  useEffect(() => {
    // Extension probe is race-prone: isExtensionInstalled() reads a flag set
    // by the extension's page-world inject script, which may not have run by
    // 500ms (esp. on cold loads / slow devices / right after an extension
    // install where the reload happens mid-boot). A single-shot check leaves
    // the UI in a permanent "not installed" state until the user reloads.
    //
    // Poll every 500ms for up to 15s (30 attempts). Stop early once
    // detected; if we never detect, fall back to whatever the check returns
    // at the final tick (false) and stop — the rest of the UI handles the
    // "install the extension" state gracefully.
    // Probe every 500ms for up to 15s (30 × 500) so the extension's
    // content-script injection — which happens at document_idle — has a chance
    // to land before we give up and show the "install the extension" UI.
    const EXT_PROBE_INTERVAL_MS = 500
    const EXT_PROBE_MAX_ATTEMPTS = 30
    let attempts = 0
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const probe = () => {
      if (cancelled) return
      const installed = isExtensionInstalled()
      setExtInstalled(installed)
      setExtVersion(getExtensionVersion())
      if (installed) {
        sendCommand<{ hasSalesNav: boolean; hasRecruiter?: boolean }>('GET_FEATURES')
          .then((r) => {
            if (cancelled) return
            setHasSalesNav(r?.hasSalesNav || false)
            // hasRecruiter may not exist on older extensions — treat
            // a missing key as "no Recruiter account" so the sidebar
            // item stays hidden for pre-1.10 extension builds.
            setHasRecruiter(r?.hasRecruiter || false)
          })
          .catch((err) => console.warn('[Shell] GET_FEATURES failed:', err?.message))
        return // stop polling once detected
      }
      attempts++
      if (attempts < EXT_PROBE_MAX_ATTEMPTS) {
        timer = setTimeout(probe, EXT_PROBE_INTERVAL_MS)
      }
    }
    timer = setTimeout(probe, EXT_PROBE_INTERVAL_MS)
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [])

  // Measure the button + close on scroll/resize so the popover can't drift
  // away from its anchor. Scroll listener is non-capturing — previously
  // `capture: true` caught scroll events from the nested conversation
  // list, so scrolling the list while aiming at a menu item dismissed
  // the menu mid-click (review LOW finding). The header itself doesn't
  // scroll, so window-level bubbling is the correct granularity: if
  // the page scrolls, close the menu; descendant scroll containers
  // don't affect the header-anchored button's viewport position
  // materially.
  useLayoutEffect(() => {
    if (!inboxViewMenuOpen) { setFilterMenuPos(null); return }
    const btn = filterBtnRef.current
    if (!btn) return
    const rect = btn.getBoundingClientRect()
    setFilterMenuPos({ top: rect.bottom + 6, right: Math.max(8, window.innerWidth - rect.right) })
    const close = () => setInboxViewMenuOpen(false)
    window.addEventListener('resize', close)
    window.addEventListener('scroll', close)
    return () => {
      window.removeEventListener('resize', close)
      window.removeEventListener('scroll', close)
    }
  }, [inboxViewMenuOpen])

  // Central filter-navigation hook. The legacy 'UNREAD' filter key plus the
  // new 'WAITING_THEIRS' / 'WAITING_MINE' aliases are not real sidebar
  // filters — they are Inbox view modes. This helper translates any of them
  // into (activeFilter='INBOX', inboxView=<mode>) so callers (sidebar,
  // OrganizeModal, CommandPalette, keyboard shortcuts, palette-invoked
  // preference defaults) stay path-consistent. Navigating to a real
  // sidebar filter (STARRED, ARCHIVE, tag:..., etc.) does not clear
  // inboxView — the mode is a sticky view preference of the Inbox.
  const navigateToFilter = useCallback((filter: string) => {
    // Single navigate so we don't push two history entries. INBOX view
    // aliases (UNREAD / WAITING_THEIRS / WAITING_MINE) go to /inbox/:view
    // directly; everything else goes to /<filter> via buildFilterRoute.
    if (INBOX_VIEW_ALIASES.has(filter)) {
      navigate(buildInboxRoute(filter as InboxView))
    } else {
      navigate(buildFilterRoute(filter))
    }
  }, [navigate])

  const categoryId = activeFilter.startsWith('category:')
    ? activeFilter.slice('category:'.length)
    : null

  const { assignments } = useCategoryAssignments()
  const { bookmarkedUrns, toggleBookmark } = useBookmarks()

  const categoryProfileUrns = useMemo(() => {
    if (!categoryId) return null
    return new Set(
      assignments
        .filter((a) => a.category_id === categoryId)
        .map((a) => a.contact_profile_urn)
    )
  }, [categoryId, assignments])

  const [allConversations, setAllConversations] = useState<Conversation[]>([])
  const allConversationsRef = useLatestRef(allConversations)
  const conversationsRef = useRef<Conversation[]>([])

  // --- Archive/Unarchive (extracted to useArchiveUndo) ---
  // Called here (before conversations useMemo) because conversations depends on exitingUrns.
  // The hook reads conversations via a ref to break the circular dependency.
  const {
    exitingUrns, archiveToast, handleArchive, handleUnarchive,
    handleBatchArchive: _handleBatchArchive, handleBatchUnarchive: _handleBatchUnarchive,
    handleExitComplete, handleUndoArchive, dismissArchiveToast,
  } = useArchiveUndo({
    allConversationsRef,
    setAllConversations,
    selectedUrn,
    setSelectedUrn,
    conversationsRef,
  })

  // Read conversations from IndexedDB — only update state if data changed
  const lastConvHashRef = useRef('')
  const [initialized, setInitialized] = useState(false)
  const fetchConversations = useCallback(async () => {
    const convs = await db.conversations.orderBy('lastMessageTimestamp').reverse().toArray()
    // Quick hash to avoid unnecessary re-renders
    const hash = convs.map(c => `${c.conversationUrn}:${c.lastMessageTimestamp}:${c.isRead}:${c.unreadCount}:${c.category}:${c.lastMessageText}`).join('|')
    if (hash !== lastConvHashRef.current) {
      lastConvHashRef.current = hash
      setAllConversations(convs)
    }
    if (!initialized) setInitialized(true)
  }, [initialized])

  // Initial load
  useEffect(() => {
    fetchConversations()
  }, [fetchConversations])

  // Active listener: re-read when content-data.js writes new conversation data
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail
      if (detail?.type === 'conversations' || detail?.type === 'messages') {
        fetchConversations()
      }
    }
    window.addEventListener('fwdslash-data-updated', handler)
    return () => window.removeEventListener('fwdslash-data-updated', handler)
  }, [fetchConversations])

  // Close pickers when switching conversations
  useEffect(() => {
    setShowReminderPicker(false)
    setShowTagPicker(false)
  }, [selectedUrn])

  // --- Inbox person category filter ---
  // Hoisted above the filter-change effect below so `setInboxCategory`
  // is lexically declared before the effect body references it. Prior
  // placement was later in the file; the effect ran after all useState
  // calls at runtime so it worked, but the react-hooks lint rule flagged
  // a TDZ violation that a future reorder could turn into a real bug.
  const [inboxCategory, setInboxCategory] = useState<string>('ALL')

  // Clear selection + reset the person-category chip when switching
  // filters. Sidebar's onFilterChange does this explicitly too, but
  // CommandPalette / OrganizeModal navigate paths don't — hoisting the
  // reset into a filter-change effect covers all entry points. Search
  // stays per-filter (some filters genuinely want to keep a search
  // scope); sidebar still calls `search.exitSearch()` explicitly.
  useEffect(() => {
    setSelectedUrns(new Set())
    setInboxCategory('ALL')
  }, [activeFilter])

  // Clean up stale selections when conversations change
  useEffect(() => {
    const validUrns = new Set(allConversations.map((c) => c.conversationUrn))
    setSelectedUrns((prev) => {
      const cleaned = new Set([...prev].filter((u) => validUrns.has(u)))
      return cleaned.size === prev.size ? prev : cleaned
    })
  }, [allConversations])

  const conversations = useMemo(() => {
    // Only keep exiting conversations visible in the filter they're leaving FROM
    // 'archive' intent = leaving a non-archive view; 'unarchive' intent = leaving archive view
    const exitIntent = activeFilter === 'ARCHIVE' ? 'unarchive' : 'archive'
    const exiting = exitingUrns.size > 0
      ? (urn: string) => exitingUrns.get(urn) === exitIntent
      : null
    let filtered: Conversation[]
    if (activeFilter === 'REMINDERS') {
      filtered = allConversations.filter((c) => remindersHook.reminderUrns.has(c.conversationUrn) || exiting?.(c.conversationUrn))
      // Sort by reminder time ascending (soonest first)
      filtered.sort((a, b) => {
        const aTime = remindersHook.getReminderTime(a.conversationUrn) || Infinity
        const bTime = remindersHook.getReminderTime(b.conversationUrn) || Infinity
        return aTime - bTime
      })
    } else if (activeFilter === 'STARRED') {
      filtered = allConversations.filter((c) => bookmarkedUrns.has(c.conversationUrn) || exiting?.(c.conversationUrn))
    } else if (activeFilter === 'DRAFTS') {
      filtered = allConversations.filter((c) => draftUrns.has(c.conversationUrn) || exiting?.(c.conversationUrn))
    } else if (activeFilter.startsWith('tag:')) {
      const tagId = activeFilter.replace('tag:', '')
      const taggedUrns = tagAssignmentsHook.getConversationsForTag(tagId)
      filtered = allConversations.filter((c) => taggedUrns.has(c.conversationUrn) || exiting?.(c.conversationUrn))
    } else if (activeFilter.startsWith('composite:')) {
      // Saved Inbox Composite (ADR 0029). Evaluates the stored predicate
      // tree against each conversation. If the composite id resolves to
      // nothing (deleted on another tab before the realtime fan-in
      // lands, or a stale URL in history), fall back to the empty list —
      // better than the full inbox with no filter applied.
      const compositeId = activeFilter.slice('composite:'.length)
      const composite = compositesHook.composites.find((c) => c.id === compositeId)
      if (!composite) {
        filtered = []
      } else {
        const ctx = {
          getTagsForConversation: tagAssignmentsHook.getTagsForConversation,
          isStarred: (urn: string) => bookmarkedUrns.has(urn),
        }
        filtered = allConversations.filter(
          (c) => evaluateComposite(composite.definition, c, ctx) || exiting?.(c.conversationUrn),
        )
      }
    } else if (activeFilter === 'UNTAGGED') {
      const allUrns = allConversations.map((c) => c.conversationUrn)
      const untagged = tagAssignmentsHook.getUntaggedConversationUrns(allUrns)
      filtered = allConversations.filter((c) => untagged.has(c.conversationUrn) || exiting?.(c.conversationUrn))
    } else if (categoryProfileUrns) {
      filtered = allConversations.filter((c) => categoryProfileUrns.has(c.participantProfileUrn) || exiting?.(c.conversationUrn))
    } else if (activeFilter === 'ARCHIVE') {
      filtered = allConversations.filter((c) => (c.category === 'ARCHIVE' && !remindersHook.reminderUrns.has(c.conversationUrn)) || exiting?.(c.conversationUrn))
    } else if (activeFilter === 'SALESNAV_ARCHIVE') {
      // Sales Nav parallel archive (kondo-parity 1.9). An archived
      // Sales Nav conversation keeps `previousCategory === 'SALESNAV'`;
      // we use that instead of the live `category` because archival
      // flips category to ARCHIVE on both regular and Sales Nav
      // threads. The previousCategory check cleanly disambiguates
      // "Sales Nav thread currently archived" from "regular thread
      // currently archived".
      filtered = allConversations.filter((c) =>
        (c.category === 'ARCHIVE' && c.previousCategory === 'SALESNAV' && !remindersHook.reminderUrns.has(c.conversationUrn))
        || exiting?.(c.conversationUrn),
      )
    } else if (activeFilter === 'RECRUITER_ARCHIVE') {
      // Recruiter parallel archive (kondo-parity 1.10). Same shape as
      // SALESNAV_ARCHIVE — `previousCategory === 'RECRUITER'` is the
      // only way to tell a recruiter thread currently in ARCHIVE from
      // a regular-inbox thread currently in ARCHIVE.
      filtered = allConversations.filter((c) =>
        (c.category === 'ARCHIVE' && c.previousCategory === 'RECRUITER' && !remindersHook.reminderUrns.has(c.conversationUrn))
        || exiting?.(c.conversationUrn),
      )
    } else {
      filtered = allConversations.filter((c) => c.category === activeFilter || exiting?.(c.conversationUrn))
    }

    // Inbox-scoped view mode (Kondo-style filter menu). Only applied on the
    // Inbox filter — Archive / Drafts / tag filters ignore the mode since
    // they have their own list semantics. Exiting conversations pass
    // through unconditionally so the slide-out animation isn't cut short
    // when the user marks the item read / replies.
    //
    // WAITING_THEIRS / WAITING_MINE read `lastMessageFromMe` — a boolean
    // written at sync-time by the extension (which always knows the viewer
    // URN). See docs/adr/0002-write-time-conversation-denormalization.md.
    // Rows with `lastMessageFromMe === undefined` (older extension versions,
    // Sales Nav conversations where the sales identity wasn't resolvable,
    // empty threads) naturally drop out of both waiting views. The next
    // extension sync backfills them.
    if (activeFilter === 'INBOX' && inboxView !== 'ALL') {
      filtered = filtered.filter((c) => {
        if (exiting?.(c.conversationUrn)) return true
        if (inboxView === 'UNREAD') return !c.isRead
        if (inboxView === 'WAITING_THEIRS') return c.lastMessageFromMe === true
        if (inboxView === 'WAITING_MINE') return c.lastMessageFromMe === false
        return true
      })
    }

    // Keep the selected conversation visible ONLY if it was already in this filter
    // (prevents crash when sending from Drafts clears the draft mid-view)
    // Don't inject it when the user manually switches filters — they expect a clean list
    if (selectedUrn && !filtered.some((c) => c.conversationUrn === selectedUrn)) {
      // Check if the conversation belongs in this filter naturally
      const selected = allConversations.find((c) => c.conversationUrn === selectedUrn)
      if (selected) {
        const belongsInFilter =
          (activeFilter === 'INBOX' && selected.category === 'INBOX') ||
          (activeFilter === 'ARCHIVE' && selected.category === 'ARCHIVE') ||
          (activeFilter === 'INMAIL' && selected.category === 'INMAIL') ||
          (activeFilter === 'SPONSORED' && selected.category === 'SPONSORED') ||
          (activeFilter === 'SPAM' && selected.category === 'SPAM') ||
          (activeFilter === 'SALESNAV' && selected.category === 'SALESNAV') ||
          (activeFilter === 'RECRUITER' && selected.category === 'RECRUITER') ||
          (activeFilter.startsWith('tag:') && tagAssignmentsHook.getConversationsForTag(activeFilter.replace('tag:', '')).has(selected.conversationUrn))
        if (belongsInFilter) {
          filtered = [selected, ...filtered]
        }
      }
    }

    return filtered
  }, [allConversations, activeFilter, inboxView, categoryProfileUrns, bookmarkedUrns, draftUrns, selectedUrn, exitingUrns, remindersHook.reminderUrns, remindersHook.getReminderTime, tagAssignmentsHook.getConversationsForTag, tagAssignmentsHook.getUntaggedConversationUrns, tagAssignmentsHook.getTagsForConversation, compositesHook.composites])
  // Forward-declared ref (L446): useArchiveUndo needs the ref object
  // BEFORE `conversations` is computed, to break the circular dep.
  // Can't use `useLatestRef` here because declaration site ≠ assign
  // site. The ref-during-render is the stable, established shape.
  // eslint-disable-next-line react-hooks/refs
  conversationsRef.current = conversations

  const handleConnectionClick = useCallback(async (connection: Connection) => {
    // Find ALL matching conversations — regular messaging + Sales Nav
    const candidates: Conversation[] = []

    // Regular messaging (indexed lookup)
    const regularConvs = await db.conversations.where('participantProfileUrn').equals(connection.profileUrn).toArray()
    candidates.push(...regularConvs)

    // Sales Nav (in-memory scan — different profileUrn format)
    const salesNavMatches = allConversations.filter((c) =>
      c.category === 'SALESNAV' && c.allParticipants?.some((p) =>
        p.name === connection.name || p.profileUrn === connection.profileUrn
      )
    )
    // Dedupe: only add Sales Nav matches not already in candidates
    const candidateUrns = new Set(candidates.map((c) => c.conversationUrn))
    for (const snc of salesNavMatches) {
      if (!candidateUrns.has(snc.conversationUrn)) candidates.push(snc)
    }

    if (candidates.length > 0) {
      // Pick the most recently active conversation. Single navigate to
      // land on the conv in its own filter. Firing side effects (mark-
      // read, reminder-clear) separately from the navigation prevents a
      // double history push.
      const best = candidates.sort((a, b) => b.lastMessageTimestamp - a.lastMessageTimestamp)[0]
      const filter = best.category === 'SALESNAV' ? 'SALESNAV' : best.category === 'INMAIL' ? 'INMAIL' : 'INBOX'
      if (filter === 'INBOX') {
        navigate(buildInboxRoute(inboxView, best.conversationUrn))
      } else {
        navigate(buildFilterRoute(filter, best.conversationUrn))
      }
      selectConversationSideEffects(best.conversationUrn)
      return
    }

    // No existing conv — open compose, prefilled with the recipient.
    // Single navigate to /compose; composeRecipient lives in component
    // state (not URL) since it's a short-lived prefill.
    setComposeRecipient(connection)
    navigate(buildComposeRoute())
  }, [navigate, inboxView, selectConversationSideEffects, allConversations])

  const handleMultiSelect = useCallback((conversationUrn: string, event: React.MouseEvent) => {
    setActivePane('list')
    if (event.shiftKey && lastClickedUrn.current) {
      const allUrns = navListRef.current.map((c) => c.conversationUrn)
      const startIdx = allUrns.indexOf(lastClickedUrn.current)
      const endIdx = allUrns.indexOf(conversationUrn)
      if (startIdx !== -1 && endIdx !== -1) {
        const lo = Math.min(startIdx, endIdx)
        const hi = Math.max(startIdx, endIdx)
        const rangeUrns = allUrns.slice(lo, hi + 1)
        setSelectedUrns((prev) => {
          const next = new Set(prev)
          for (const urn of rangeUrns) next.add(urn)
          return next
        })
      }
    } else {
      setSelectedUrns((prev) => {
        const next = new Set(prev)
        if (next.has(conversationUrn)) next.delete(conversationUrn)
        else next.add(conversationUrn)
        return next
      })
    }
    lastClickedUrn.current = conversationUrn
  }, [])

  const handleGroupSelect = useCallback((urns: string[]) => {
    setActivePane('list')
    setSelectedUrns((prev) => {
      const allAlreadySelected = urns.every((u) => prev.has(u))
      const next = new Set(prev)
      if (allAlreadySelected) {
        for (const u of urns) next.delete(u)
      } else {
        for (const u of urns) next.add(u)
      }
      return next
    })
  }, [])

  // Wrap batch handlers to also clear multi-select
  const handleBatchArchive = useCallback((urns: Set<string>) => {
    _handleBatchArchive(urns)
    setSelectedUrns(new Set())
  }, [_handleBatchArchive])

  const handleBatchUnarchive = useCallback((urns: Set<string>) => {
    _handleBatchUnarchive(urns)
    setSelectedUrns(new Set())
  }, [_handleBatchUnarchive])

  const handleBatchMarkRead = useCallback((urns: Set<string>) => {
    const allRead = [...urns].every((urn) => allConversations.find((c) => c.conversationUrn === urn)?.isRead)
    const newRead = !allRead
    setAllConversations((prev) => prev.map((c) =>
      urns.has(c.conversationUrn) ? { ...c, isRead: newRead, unreadCount: newRead ? 0 : 1 } : c
    ))
    const urnArray = [...urns]
    // Voyager tolerates 5 concurrent mark-read calls; more triggers rate limits.
    // 200ms between batches keeps us well under LinkedIn's burst threshold while
    // still clearing a 50-selection batch in under 3s.
    const MARK_READ_CHUNK_SIZE = 5
    const MARK_READ_CHUNK_DELAY_MS = 200
    const chunks: string[][] = []
    for (let i = 0; i < urnArray.length; i += MARK_READ_CHUNK_SIZE) {
      chunks.push(urnArray.slice(i, i + MARK_READ_CHUNK_SIZE))
    }
    ;(async () => {
      for (const chunk of chunks) {
        await Promise.allSettled(chunk.map((urn) => sendCommand(newRead ? 'MARK_READ' : 'MARK_UNREAD', { conversationUrn: urn })))
        if (chunks.length > 1) await new Promise((r) => setTimeout(r, MARK_READ_CHUNK_DELAY_MS))
      }
    })()
    setSelectedUrns(new Set())
  }, [allConversations])

  const handleBatchStar = useCallback((urns: Set<string>) => {
    const allStarred = [...urns].every((urn) => bookmarkedUrns.has(urn))
    if (allStarred) { for (const urn of urns) voidAsync(toggleBookmark(urn), 'toggleBookmark (batch off)', { urn }) }
    else { for (const urn of urns) { if (!bookmarkedUrns.has(urn)) voidAsync(toggleBookmark(urn), 'toggleBookmark (batch on)', { urn }) } }
    setSelectedUrns(new Set())
  }, [bookmarkedUrns, toggleBookmark])

  // --- Reminders ---
  const [reminderToast, setReminderToast] = useState<{ urn: string; time: string } | null>(null)
  const reminderToastTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  const handleRemind = useCallback((conversationUrn: string, remindAt: Date) => {
    voidAsync(remindersHook.setReminder(conversationUrn, remindAt), 'setReminder', { urn: conversationUrn, remindAt: remindAt.toISOString() })
    handleArchive(conversationUrn)

    const timeStr = remindAt.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })
      + ' ' + remindAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    if (reminderToastTimer.current) clearTimeout(reminderToastTimer.current)
    setReminderToast({ urn: conversationUrn, time: timeStr })
    // Override the archive toast with reminder toast
    dismissArchiveToast()
    // Reminder toast stays visible longer than the default archive toast —
    // users expect a beat to Cmd-Z a mis-timed reminder before it slides away.
    const REMINDER_TOAST_DURATION_MS = 12_000
    reminderToastTimer.current = setTimeout(() => setReminderToast(null), REMINDER_TOAST_DURATION_MS)
  }, [remindersHook, handleArchive, dismissArchiveToast])

  const handleUndoReminder = useCallback(() => {
    if (!reminderToast) return
    voidAsync(remindersHook.clearReminder(reminderToast.urn), 'clearReminder (undo)', { urn: reminderToast.urn })
    handleUnarchive(reminderToast.urn)
    setSelectedUrn(reminderToast.urn)
    setReminderToast(null)
    if (reminderToastTimer.current) clearTimeout(reminderToastTimer.current)
  }, [reminderToast, remindersHook, handleUnarchive])

  // Cleanup reminder toast timer on unmount
  useEffect(() => {
    return () => {
      if (reminderToastTimer.current) clearTimeout(reminderToastTimer.current)
    }
  }, [])

  // Depend on the raw `assignments` array, not the `getConversationsForTag`
  // callback — that callback is re-created every render (identity-unstable),
  // which invalidated the memo and the O(tags * assignments) recomputation
  // ran on every Shell render. Computing in one pass over assignments is
  // also O(assignments), vs. O(tags * assignments) for the per-tag-filter
  // approach. Track unique conversation URNs per tag to match the old
  // Set-based behaviour in case the assignments array ever has dupes.
  const tagAssignments = tagAssignmentsHook.assignments
  const tagCounts = useMemo(() => {
    const urnsByTag = new Map<string, Set<string>>()
    for (const tag of tagsHook.tags) urnsByTag.set(tag.id, new Set())
    for (const a of tagAssignments) {
      urnsByTag.get(a.tag_id)?.add(a.conversation_urn)
    }
    const counts: Record<string, number> = {}
    for (const [tagId, urns] of urnsByTag) counts[tagId] = urns.size
    return counts
  }, [tagsHook.tags, tagAssignments])

  // Composite counts (ADR 0029). Computed separately from `conversations`
  // (which is already filtered by the active view) so the sidebar always
  // reflects the full `allConversations` set against each saved filter.
  // Evaluator is pure + O(n·m) for n conversations × m composites; fine
  // for typical inbox sizes.
  const compositeCounts = useMemo(() => {
    if (compositesHook.composites.length === 0) return {}
    const ctx = {
      getTagsForConversation: tagAssignmentsHook.getTagsForConversation,
      isStarred: (urn: string) => bookmarkedUrns.has(urn),
    }
    const counts: Record<string, number> = {}
    for (const composite of compositesHook.composites) {
      let n = 0
      for (const c of allConversations) {
        if (evaluateComposite(composite.definition, c, ctx)) n++
      }
      counts[composite.id] = n
    }
    return counts
  }, [compositesHook.composites, allConversations, tagAssignmentsHook.getTagsForConversation, bookmarkedUrns])

  const inboxCategoryCounts = useMemo(() => {
    const counts: Record<string, number> = { ALL: conversations.length }
    for (const c of conversations) {
      const cat = categorizeByHeadline(c.participantHeadline)
      counts[cat] = (counts[cat] || 0) + 1
    }
    return counts
  }, [conversations])

  const filteredConversations = useMemo(() => {
    if (inboxCategory === 'ALL') return conversations
    return conversations.filter((c) => categorizeByHeadline(c.participantHeadline) === inboxCategory)
  }, [conversations, inboxCategory])

  // The list j/k navigation operates on (search results or filtered conversations)
  const navList = search.searchMode ? search.results : filteredConversations
  // Forward-declared ref (L235): keyboard-shortcut handlers read it
  // synchronously and it must be present on first render. Same
  // rationale as conversationsRef above.
  // eslint-disable-next-line react-hooks/refs
  navListRef.current = navList

  // --- Global keyboard shortcuts ---
  const suppressShortcuts = organizeOpen || shortcutsHelpOpen || commandPaletteOpen
  const shortcutHandler = useCallback((e: KeyboardEvent) => {
    const key = e.key

    // Cmd/Ctrl+A: select all (list pane) or copy conversation (thread pane)
    if ((e.metaKey || e.ctrlKey) && key === 'a' && !isEditableElement(e.target)) {
      e.preventDefault()
      if (activePaneRef.current === 'list') {
        const allUrns = navList.map((c) => c.conversationUrn)
        const allSelected = allUrns.length > 0 && allUrns.every((u) => selectedUrns.has(u))
        setSelectedUrns(allSelected ? new Set() : new Set(allUrns))
      } else {
        threadCopyRef.current?.()
      }
      return
    }

    // Escape: pane-aware cascade
    if (key === 'Escape') {
      if (inboxViewMenuOpen) { setInboxViewMenuOpen(false); return }
      if (commandPaletteOpen) { setCommandPaletteOpen(false); return }
      if (shortcutsHelpOpen) { setShortcutsHelpOpen(false); return }
      if (organizeOpen) { setOrganizeOpen(false); return }
      if (showReminderPicker) { setShowReminderPicker(false); return }
      if (showTagPicker) { setShowTagPicker(false); return }
      if (composeMode) { setComposeMode(false); setComposeRecipient(null); return }
      if (activePaneRef.current === 'thread') { setActivePane('list'); return }
      if (selectedUrns.size > 0) { setSelectedUrns(new Set()); return }
      if (search.searchMode) { search.exitSearch(); return }
      if (selectedUrn) { setSelectedUrn(null); return }
      return
    }

    // Pass through other Cmd/Ctrl combos to browser
    if (e.ctrlKey || e.metaKey || e.altKey) return

    if (isEditableElement(e.target)) return
    if (suppressShortcuts) return

    if (composeMode) {
      if (key === '?') { e.preventDefault(); setShortcutsHelpOpen(true) }
      return
    }

    // "g" prefix for go-to navigation (Gmail-style).
    // Must run AFTER composeMode guard so `g s` doesn't navigate away mid-draft,
    // but BEFORE the settings/templates guard so users can `g i` back to inbox.
    if (gKeyPressed.current && Date.now() - gKeyPressed.current < 1000) {
      gKeyPressed.current = 0
      if (key === 's') { e.preventDefault(); setActivePage('settings'); return }
      if (key === 'i') { e.preventDefault(); setActivePage('inbox'); return }
      if (key === 't') { e.preventDefault(); setActivePage('templates'); return }
    }
    if (key === 'g') {
      e.preventDefault()
      gKeyPressed.current = Date.now()
      return
    }

    if (activeFilter === 'INVITES' || activeFilter === 'CONNECTIONS' || activePage === 'templates' || activePage === 'settings') {
      if (key === '?') { e.preventDefault(); setShortcutsHelpOpen(true) }
      return
    }

    // Inbox view-mode shortcuts (Kondo parity). Shift+A/S/W/Q cycle through
    // the filter menu's items. Only meaningful on the INBOX filter — on
    // other filters they fall through to the normal switch below (no S/W/Q
    // bindings collide; `s` is lowercase-bookmark, distinct from Shift+S).
    if (e.shiftKey && activeFilter === 'INBOX') {
      if (key === 'A') { e.preventDefault(); setInboxView('ALL'); return }
      if (key === 'Q') { e.preventDefault(); setInboxView('UNREAD'); return }
      if (key === 'S') { e.preventDefault(); setInboxView('WAITING_THEIRS'); return }
      if (key === 'W') { e.preventDefault(); setInboxView('WAITING_MINE'); return }
    }

    switch (key) {
      case 'j': {
        e.preventDefault()
        const currentIndex = navList.findIndex((c) => c.conversationUrn === highlightedUrn)
        const nextIndex = currentIndex === -1 ? 0 : Math.min(currentIndex + 1, navList.length - 1)
        const nextUrn = navList[nextIndex]?.conversationUrn
        if (nextUrn) {
          if (e.shiftKey) {
            setSelectedUrns((prev) => {
              const next = new Set(prev)
              if (highlightedUrn) next.add(highlightedUrn)
              next.add(nextUrn)
              return next
            })
          }
          setHighlightedUrn(nextUrn)
        }
        break
      }
      case 'k': {
        e.preventDefault()
        const currentIndex = navList.findIndex((c) => c.conversationUrn === highlightedUrn)
        const prevIndex = currentIndex === -1 ? 0 : Math.max(currentIndex - 1, 0)
        const prevUrn = navList[prevIndex]?.conversationUrn
        if (prevUrn) {
          if (e.shiftKey) {
            setSelectedUrns((prev) => {
              const next = new Set(prev)
              if (highlightedUrn) next.add(highlightedUrn)
              next.add(prevUrn)
              return next
            })
          }
          setHighlightedUrn(prevUrn)
        }
        break
      }
      case 'Enter': {
        e.preventDefault()
        if (highlightedUrn) handleSelectConversation(highlightedUrn)
        break
      }
      case 'x':
      case ' ': {
        if (activePaneRef.current !== 'list') break
        e.preventDefault()
        const toggleUrn = highlightedUrn || selectedUrn
        if (!toggleUrn) break
        setSelectedUrns((prev) => {
          const next = new Set(prev)
          if (next.has(toggleUrn)) next.delete(toggleUrn)
          else next.add(toggleUrn)
          return next
        })
        break
      }
      case 'e': {
        e.preventDefault()
        if (selectedUrns.size > 0) {
          if (activeFilter === 'ARCHIVE') handleBatchUnarchive(selectedUrns)
          else handleBatchArchive(selectedUrns)
        } else {
          const targetUrn = selectedUrn || highlightedUrn
          if (!targetUrn) return
          if (activeFilter === 'ARCHIVE') handleUnarchive(targetUrn)
          else handleArchive(targetUrn)
        }
        break
      }
      case 'E': {
        if (!e.shiftKey) break
        e.preventDefault()
        if (selectedUrns.size > 0) handleBatchUnarchive(selectedUrns)
        else {
          const targetUrn = selectedUrn || highlightedUrn
          if (targetUrn) handleUnarchive(targetUrn)
        }
        break
      }
      case 'u': {
        e.preventDefault()
        if (selectedUrns.size > 0) {
          handleBatchMarkRead(selectedUrns)
        } else {
          const targetUrn = selectedUrn || highlightedUrn
          if (!targetUrn) return
          const conv = allConversations.find((c) => c.conversationUrn === targetUrn)
          if (!conv) return
          const newRead = !conv.isRead
          setAllConversations((prev) => prev.map((c) =>
            c.conversationUrn === targetUrn ? { ...c, isRead: newRead, unreadCount: newRead ? 0 : 1 } : c
          ))
          sendCommand(newRead ? 'MARK_READ' : 'MARK_UNREAD', { conversationUrn: targetUrn }).catch((err) => console.warn('[Shell] MARK_READ/UNREAD failed:', err?.message))
        }
        break
      }
      case 's': {
        e.preventDefault()
        if (selectedUrns.size > 0) handleBatchStar(selectedUrns)
        else {
          const targetUrn = selectedUrn || highlightedUrn
          if (targetUrn) voidAsync(toggleBookmark(targetUrn), 'toggleBookmark (shortcut)', { urn: targetUrn })
        }
        break
      }
      case 't': {
        e.preventDefault()
        if (selectedUrns.size > 0) {
          setBatchTagUrns(new Set(selectedUrns))
          setShowTagPicker(true)
        } else if (selectedUrn) {
          setBatchTagUrns(null)
          setShowTagPicker(true)
        }
        break
      }
      case 'h': { e.preventDefault(); if (selectedUrn) setShowReminderPicker(true); break }
      case 'l': { e.preventDefault(); setOrganizeOpen(true); break }
      case 'r': {
        e.preventDefault()
        if (selectedUrn) {
          setActivePane('thread')  // also syncs ref
          const textarea = document.querySelector<HTMLTextAreaElement>('[data-compose-textarea]')
          textarea?.focus()
        }
        break
      }
      case 'c': { e.preventDefault(); setComposeMode(true); break }
      case '/': { e.preventDefault(); setCommandPaletteOpen(true); break }
      case '?': { e.preventDefault(); setShortcutsHelpOpen(true); break }
    }
  }, [
    shortcutsHelpOpen, organizeOpen, showReminderPicker, showTagPicker, composeMode,
    search.searchMode, search.exitSearch, selectedUrn, highlightedUrn,
    activeFilter, activePage, navList, allConversations,
    selectedUrns, bookmarkedUrns, suppressShortcuts,
    handleSelectConversation, handleArchive, handleUnarchive, handleBatchArchive, handleBatchUnarchive, toggleBookmark,
  ])

  useShortcuts(shortcutHandler)

  return (
    <div className="flex h-svh flex-col bg-zinc-50">
      {extensionInvalidated && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-sm text-amber-800">
          Extension was updated — <button onClick={() => window.location.reload()} className="underline font-medium">reload this page</button> to reconnect.
        </div>
      )}
      {!extensionInvalidated && extInstalled === false && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-sm text-amber-800">
          Slash extension not detected. Install it to connect to LinkedIn.
        </div>
      )}
      {!extensionInvalidated && extInstalled && !isExtensionVersionSupported(extVersion) && (
        <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-center text-sm text-red-800">
          Slash extension v{extVersion} is too old to run this app. Update to v{MIN_EXTENSION_VERSION} or later — go to <code className="font-mono text-xs">chrome://extensions</code>, enable Developer mode, and click Update.
        </div>
      )}
      {!extensionInvalidated && extInstalled && isExtensionUpdateAvailable(extVersion) && (
        <div className="border-b border-blue-200 bg-blue-50 px-4 py-2 text-center text-sm text-blue-800">
          Slash extension update available: v{extVersion} → v{LATEST_EXTENSION_VERSION}. Open <code className="font-mono text-xs">chrome://extensions</code> and click Update to get the latest.
        </div>
      )}
      {extensionUnresponsive && (
        <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-center text-sm text-red-800">
          Extension not responding — try reloading it in chrome://extensions
        </div>
      )}
      {isStale && !extensionUnresponsive && (
        <div className="border-b border-zinc-200 bg-zinc-100 px-4 py-2 text-center text-sm text-zinc-500">
          Reconnecting to LinkedIn...
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        <ErrorBoundary name="shell:sidebar" fallback={
          <aside className="flex w-[260px] items-center justify-center border-r border-zinc-200 bg-white text-xs text-zinc-400">
            Sidebar error
          </aside>
        }>
        <Sidebar
          activeFilter={activeFilter}
          onFilterChange={(f) => { navigateToFilter(f); search.exitSearch(); setInboxCategory('ALL') }}
          onNavigate={setActivePage}
          activePage={activePage}
          searchQuery={search.searchQuery}
          onSearchChange={search.setSearchQuery}
          onSearchFocus={search.enterSearch}
          onSearchEscape={search.exitSearch}
          searchMode={search.searchMode}
          unreadCount={allConversations.filter((c) => !c.isRead && c.category !== 'ARCHIVE' && c.category !== 'SPONSORED' && c.category !== 'SPAM').length}
          draftCount={draftCount}
          inviteCount={invitesHook.inviteCount}
          reminderCount={remindersHook.dueCount}
          hasSalesNav={hasSalesNav}
          hasRecruiter={hasRecruiter}
          tags={tagsHook.tags}
          tagCounts={tagCounts}
          composites={compositesHook.composites}
          compositeCounts={compositeCounts}
          onOrganizeOpen={() => setOrganizeOpen(true)}
        />
        </ErrorBoundary>

        <ErrorBoundary name="shell:main">
        {/* No `key` on this wrapper: previously the whole main column
            remounted on every filter switch, which tore down and rebuilt the
            ThreadView (heavy tree, losing scroll position, React state,
            useLiveQuery subscriptions, etc). The fade-in the key was
            scoping to is now applied to the conversation list alone — that's
            the element that actually changes when the filter changes. */}
        <div className="flex flex-1 overflow-hidden">
        {activePage === 'settings' ? (
          <ErrorBoundary name="page:settings">
            <Settings
              onBack={() => setActivePage('inbox')}
              section={route.settingsSection as import('../components/settings/SettingsNav').SettingsTab | null}
            />
          </ErrorBoundary>
        ) : activePage === 'templates' ? (
          <ErrorBoundary name="page:templates">
            <Templates />
          </ErrorBoundary>
        ) : activeFilter === 'PENDING' ? (
          <ErrorBoundary name="page:pending">
            <PendingInvitations />
          </ErrorBoundary>
        ) : (
          <>
            {/* Conversation list — remounts on filter change for the fade-in.
                Scoping the key here instead of the outer column keeps the
                thread pane mounted across filter switches. */}
            <div key={activePage === 'inbox' ? activeFilter : activePage} className="animate-fade-in flex w-[340px] flex-col border-r border-zinc-200 bg-white" onMouseDown={() => setActivePane('list')}>
              {/* Header / Selection bar */}
              {isMultiSelect ? (
                <SelectionActionBar
                  key="selection"
                  className="animate-fade-in-fast"
                  count={selectedUrns.size}
                  onClear={() => setSelectedUrns(new Set())}
                  onArchive={() => {
                    if (activeFilter === 'ARCHIVE') handleBatchUnarchive(selectedUrns)
                    else handleBatchArchive(selectedUrns)
                  }}
                  onStar={() => handleBatchStar(selectedUrns)}
                  onTag={() => { setBatchTagUrns(new Set(selectedUrns)); setShowTagPicker(true) }}
                  onMarkRead={() => handleBatchMarkRead(selectedUrns)}
                  isArchiveView={activeFilter === 'ARCHIVE'}
                />
              ) : (
              <div key="header" className="animate-fade-in-fast flex items-center justify-between border-b border-zinc-100 px-4 py-[18px]">
                {search.searchMode ? (
                  <>
                    <div className="flex items-center gap-2">
                      <h2 className="text-sm font-semibold text-zinc-900">
                        {search.apiLoading ? 'Searching' : 'Results'}
                      </h2>
                      {search.apiLoading ? (
                        <div className="flex items-center gap-[3px] pt-0.5">
                          <div className="h-1 w-1 animate-pulse rounded-full bg-zinc-400" style={{ animationDelay: '0ms' }} />
                          <div className="h-1 w-1 animate-pulse rounded-full bg-zinc-400" style={{ animationDelay: '200ms' }} />
                          <div className="h-1 w-1 animate-pulse rounded-full bg-zinc-400" style={{ animationDelay: '400ms' }} />
                        </div>
                      ) : search.results.length > 0 ? (
                        <span className="text-xs text-zinc-400">{search.results.length} found</span>
                      ) : null}
                    </div>
                    <button onClick={search.exitSearch} className="text-[11px] text-blue-500 hover:text-blue-600">
                      Clear
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <h2 className="text-sm font-semibold text-zinc-900">
                        {getFilterLabel(activeFilter, tagsHook.tags)}
                      </h2>
                      {activeFilter === 'INBOX' && inboxView !== 'ALL' && (
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                          {INBOX_VIEW_ITEMS.find((v) => v.key === inboxView)?.label}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {activeFilter === 'INBOX' && (
                        <button
                          ref={filterBtnRef}
                          onClick={() => setInboxViewMenuOpen((v) => !v)}
                          aria-label="Filter inbox"
                          aria-haspopup="menu"
                          aria-expanded={inboxViewMenuOpen}
                          title="Filter inbox"
                          className={`flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
                            inboxView !== 'ALL'
                              ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                              : 'text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600'
                          }`}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                          </svg>
                        </button>
                      )}
                      <ActionTooltip label="New message" shortcut={<Kbd>C</Kbd>}>
                        <button
                          onClick={() => setComposeMode(true)}
                          className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                            <path d="m15 5 4 4" />
                          </svg>
                        </button>
                      </ActionTooltip>
                      {extInstalled && (
                        <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] text-green-700">
                          v{extVersion}
                        </span>
                      )}
                      <button onClick={signOut} className="text-xs text-zinc-400 hover:text-zinc-600">
                        Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
              )}

              {/* Partial-failure banner (PR 5 of code-review handoff):
                  one of the two search backends (regular LinkedIn or
                  Sales Nav) rejected while the other succeeded. Show
                  an inline note so the user knows why the result set
                  may be narrower than usual, without blocking the
                  results they DID get. */}
              {search.searchMode && search.partialFailure && search.results.length > 0 && (
                <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-[11px] text-amber-800">
                  {search.partialFailure === 'sales'
                    ? 'Sales Navigator search is currently unavailable — showing LinkedIn results only.'
                    : 'LinkedIn search is currently unavailable — showing Sales Navigator results only.'}
                </div>
              )}

              {/* Content */}
              {activeFilter === 'INVITES' ? (
                <InviteList
                  invites={invitesHook.invites}
                  loading={invitesHook.loading}
                  selectedId={selectedInviteId}
                  onSelect={setSelectedInviteId}
                  onRespond={handleInviteRespond}
                  hasMore={invitesHook.hasMore}
                  activeCategory={invitesHook.activeCategory}
                  onCategoryChange={invitesHook.setActiveCategory}
                  categoryCounts={invitesHook.categoryCounts}
                  scanning={invitesHook.scanning}
                  scannedCount={invitesHook.scannedCount}
                  scanComplete={invitesHook.scanComplete}
                  paginating={invitesHook.paginating}
                  onLoadMore={invitesHook.loadMore}
                />
              ) : activeFilter === 'CONNECTIONS' ? (
                <ConnectionsList
                  connections={connectionsHook.displayList}
                  loading={connectionsHook.loading}
                  searchQuery={connectionsHook.searchQuery}
                  onSearchChange={connectionsHook.setSearchQuery}
                  searchLoading={connectionsHook.searchLoading}
                  onSelect={handleConnectionClick}
                  hasMore={connectionsHook.hasMore}
                  paginating={connectionsHook.paginating}
                  onLoadMore={connectionsHook.loadMore}
                />
              ) : search.searchMode && search.searchQuery.trim() && search.searchError && search.results.length === 0 ? (
                /* Search failed — all backends rejected. Distinguished
                   from "no results" by the presence of searchError on
                   an empty-result set. PR 5 of the code-review handoff:
                   previously both-backends-failed looked identical to
                   zero hits. */
                <div className="flex flex-1 items-center justify-center p-6 text-center">
                  <div>
                    <p className="text-2xl text-zinc-300">⚠️</p>
                    <p className="mt-2 text-xs text-zinc-500">{search.searchError}</p>
                    <p className="mt-1 text-[11px] text-zinc-400">
                      Check your LinkedIn session in the extension and retry.
                    </p>
                  </div>
                </div>
              ) : search.searchMode && search.searchQuery.trim() && search.apiLoading && search.results.length === 0 ? (
                /* Skeleton loading state */
                <div className="flex-1 px-4 pt-3">
                  {[0.9, 0.7, 0.5, 0.35, 0.2].map((opacity, i) => (
                    <div key={i} className="flex items-start gap-3 py-3" style={{ opacity }}>
                      <div className="h-10 w-10 shrink-0 animate-pulse rounded-full bg-zinc-200" />
                      <div className="flex flex-1 flex-col gap-[6px] pt-1">
                        <div className="h-[10px] animate-pulse rounded-md bg-zinc-200" style={{ width: `${45 + (i % 3) * 10}%` }} />
                        <div className="h-[8px] animate-pulse rounded-md bg-zinc-100" style={{ width: `${65 + (i % 3) * 8}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : search.searchMode && search.searchQuery.trim() && !search.apiLoading && search.results.length === 0 ? (
                /* No results */
                <div className="flex flex-1 items-center justify-center p-6 text-center">
                  <div>
                    <p className="text-2xl text-zinc-200">🔍</p>
                    <p className="mt-2 text-xs text-zinc-400">
                      No conversations found for &ldquo;{search.searchQuery}&rdquo;
                    </p>
                  </div>
                </div>
              ) : (
                <ErrorBoundary name="pane:list">
                <ConversationList
                  conversations={search.searchMode ? search.results : filteredConversations}
                  selectedUrn={selectedUrn}
                  highlightedUrn={highlightedUrn}
                  onSelect={handleSelectConversation}
                  searchMode={search.searchMode}
                  onLoadMore={search.searchMode && search.nextCursor ? search.loadMore : undefined}
                  getDraftText={getDraftText}
                  activeFilter={activeFilter}
                  onArchive={activeFilter === 'ARCHIVE' ? handleUnarchive : handleArchive}
                  showArchiveAction={activeFilter === 'INBOX' || activeFilter === 'ARCHIVE'}
                  activeCategory={!search.searchMode ? inboxCategory : undefined}
                  onCategoryChange={!search.searchMode ? setInboxCategory : undefined}
                  categoryCounts={!search.searchMode ? inboxCategoryCounts : undefined}
                  categoryChips={!search.searchMode ? PERSON_CATEGORIES : undefined}
                  allTags={tagsHook.tags}
                  getTagsForConversation={tagAssignmentsHook.getTagsForConversation}
                  selectedUrns={selectedUrns}
                  onMultiSelect={handleMultiSelect}
                  onGroupSelect={handleGroupSelect}
                  exitingUrns={exitingUrns}
                  onExitComplete={handleExitComplete}
                  initialized={initialized}
                  inboxView={activeFilter === 'INBOX' ? inboxView : undefined}
                />
                </ErrorBoundary>
              )}
            </div>

            {/* Right panel: Thread / Compose / Invite Detail / Connection empty */}
            {activeFilter === 'INVITES' ? (
              (() => {
                const selectedInvite = invitesHook.allInvites.find((i) => i.invitationId === selectedInviteId)
                return selectedInvite ? (
                  <InviteDetail invite={selectedInvite} onRespond={handleInviteRespond} />
                ) : (
                  <div className="flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center bg-white">
                    <p className="text-2xl text-zinc-200">🤝</p>
                    <p className="mt-2 text-sm text-zinc-400">Select an invitation</p>
                  </div>
                )
              })()
            ) : activeFilter === 'CONNECTIONS' ? (
              <div className="flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center bg-white">
                <p className="text-2xl text-zinc-200">👥</p>
                <p className="mt-2 text-sm text-zinc-400">Select a connection to message</p>
              </div>
            ) : composeMode ? (
              <ErrorBoundary name="pane:compose">
              <ComposeView
                initialRecipient={composeRecipient}
                onSent={(urn, recipients) => {
                  // Create a synthetic conversation so it appears in inbox immediately
                  const primary = recipients?.[0]
                  if (primary) {
                    const synthetic: Conversation = {
                      conversationUrn: urn,
                      participantName: recipients.length > 1
                        ? recipients.map((r) => r.name).join(', ')
                        : primary.name,
                      participantHeadline: primary.headline || '',
                      participantProfileImage: primary.profileImage || null,
                      participantProfileUrn: primary.profileUrn,
                      allParticipants: recipients.map((r) => ({
                        name: r.name,
                        headline: r.headline || '',
                        profileImage: r.profileImage,
                        profileUrn: r.profileUrn,
                      })),
                      isGroup: recipients.length > 1,
                      lastMessageText: '',
                      lastMessageTimestamp: Date.now(),
                      lastMessageSenderUrn: '',
                      createdAt: Date.now(),
                      isRead: true,
                      unreadCount: 0,
                      category: 'INBOX',
                      conversationType: null,
                      threadMuted: false,
                      _cachedAt: Date.now(),
                    }
                    setAllConversations((prev) => {
                      if (prev.some((c) => c.conversationUrn === urn)) return prev
                      return [synthetic, ...prev]
                    })
                  }

                  // Switch view immediately — don't wait for LinkedIn.
                  // Single navigate to /inbox/<view>/c/<urn> so the
                  // history entry is "/compose → inbox-with-conv" rather
                  // than /compose → /inbox → /inbox/c/urn (double push).
                  setComposeRecipient(null)
                  navigate(buildInboxRoute(inboxView, urn))

                  // Reconcile with LinkedIn in the background
                  sendCommand('FETCH_MESSAGES', { conversationUrn: urn })
                    .then(() => fetchConversations())
                    .catch(() => fetchConversations())
                }}
                onClose={() => { setComposeMode(false); setComposeRecipient(null) }}
              />
              </ErrorBoundary>
            ) : (
              <div key={composeMode ? 'compose' : selectedUrn || 'empty'} className="animate-fade-in flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white" onMouseDown={() => setActivePane('thread')}>
                {selectedUrn ? (
                  <ErrorBoundary name="pane:thread">
                    <ThreadView
                      conversationUrn={selectedUrn}
                      conversations={search.searchMode ? search.results : conversations}
                      onMessageSent={removeDraftLocally}
                      onArchive={activeFilter === 'ARCHIVE' ? handleUnarchive : handleArchive}
                      onRemind={handleRemind}
                      onClearReminder={remindersHook.clearReminder}
                      isReminded={remindersHook.isReminderSet(selectedUrn)}
                      allTags={tagsHook.tags}
                      getTagsForConversation={tagAssignmentsHook.getTagsForConversation}
                      assignTag={tagAssignmentsHook.assignTag}
                      removeTagAssignment={tagAssignmentsHook.removeTagAssignment}
                      addTag={tagsHook.addTag}
                      showTagPicker={showTagPicker}
                      onSetShowTagPicker={setShowTagPicker}
                      showReminderPicker={showReminderPicker}
                      onSetShowReminderPicker={setShowReminderPicker}
                      batchTagUrns={batchTagUrns}
                      onClearBatchTag={() => setBatchTagUrns(null)}
                      copyRef={threadCopyRef}
                    />
                  </ErrorBoundary>
                ) : (
                  <div className="flex flex-1 items-center justify-center">
                    <div className="text-center">
                      <p className="text-4xl text-zinc-200">/</p>
                      <p className="mt-2 text-sm text-zinc-400">Select a conversation</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        </div>
        </ErrorBoundary>
      </div>

      {/* Archive undo toast */}
      {archiveToast && !reminderToast && (
        <div className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-lg bg-zinc-900 px-4 py-2.5 shadow-lg">
          <span className="text-xs text-white">{archiveToast.name} archived</span>
          <button
            onClick={handleUndoArchive}
            className="text-xs font-semibold text-blue-400 hover:text-blue-300"
          >
            Undo
          </button>
        </div>
      )}

      {/* Reminder undo toast */}
      {reminderToast && (
        <div className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-lg bg-zinc-900 px-4 py-2.5 shadow-lg">
          <span className="text-xs text-white">Reminder set for {reminderToast.time}</span>
          <button
            onClick={handleUndoReminder}
            className="text-xs font-semibold text-blue-400 hover:text-blue-300"
          >
            Undo
          </button>
        </div>
      )}

      {organizeOpen && (
        <OrganizeModal
          onClose={() => setOrganizeOpen(false)}
          tags={tagsHook.tags}
          onAddTag={tagsHook.addTag}
          onUpdateTag={tagsHook.updateTag}
          onDeleteTag={tagsHook.deleteTag}
          composites={compositesHook.composites}
          onAddComposite={compositesHook.addComposite}
          onDeleteComposite={compositesHook.deleteComposite}
          // Translate the active filter + inboxView into a CompositeDefinition
          // the user can name and save. Rules:
          //   - /tag/:id             → predicate [{kind:'tag', tagIds:[id], mode:'any'}]
          //   - /inbox/:view (!ALL)  → predicate [{kind:'inboxView', view}]
          //   - /inbox/:view + tag   → not reachable today (no composite
          //                             means to stack). Users build the
          //                             filter in the UI first then save.
          // Anything else (archive, starred, etc.) translates to a category
          // or status predicate, but we only seed what the user can trivially
          // get to today; the editing UX for multi-predicate composites is
          // deferred to a later iteration.
          currentCompositeDefinition={(() => {
            if (activeFilter.startsWith('tag:')) {
              const tagId = activeFilter.slice('tag:'.length)
              return {
                version: 1 as const,
                combinator: 'and' as const,
                predicates: [{ kind: 'tag' as const, tagIds: [tagId], mode: 'any' as const }],
              }
            }
            if (activeFilter === 'INBOX' && inboxView !== 'ALL') {
              return {
                version: 1 as const,
                combinator: 'and' as const,
                predicates: [{ kind: 'inboxView' as const, view: inboxView }],
              }
            }
            return { version: 1 as const, combinator: 'and' as const, predicates: [] }
          })()}
          onNavigateToFilter={(filter) => {
            // `navigateToFilter` takes us to /inbox (or /:filter) — that
            // already puts us on the inbox page. No separate setActivePage
            // needed; doubling up would stack two history entries.
            navigateToFilter(filter)
            setOrganizeOpen(false)
          }}
        />
      )}
      {shortcutsHelpOpen && <ShortcutsHelpPanel onClose={() => setShortcutsHelpOpen(false)} />}
      {/* Inbox filter-menu popover. Rendered in a portal to escape the
          conversation column's overflow-hidden ancestors (which would
          clip a 280px right-anchored menu) and the virtualized list's
          stacking context. Position is computed from the button's
          viewport rect; scroll/resize closes the menu via the effect
          above. */}
      {inboxViewMenuOpen && filterMenuPos && createPortal(
        <>
          <div
            className="fixed inset-0 z-[60]"
            onClick={() => setInboxViewMenuOpen(false)}
          />
          <div
            role="menu"
            className="animate-fade-in-fast fixed z-[61] w-[280px] overflow-hidden rounded-lg border border-zinc-200 bg-white py-1 shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
            style={{ top: filterMenuPos.top, right: filterMenuPos.right }}
          >
            {INBOX_VIEW_ITEMS.map((item) => {
              const isActive = inboxView === item.key
              return (
                <button
                  key={item.key}
                  role="menuitemradio"
                  aria-checked={isActive}
                  onClick={() => {
                    setInboxView(item.key)
                    setInboxViewMenuOpen(false)
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-zinc-700 hover:bg-zinc-50"
                >
                  <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                    {isActive && (
                      <span className="h-3 w-3 rounded-full border-[1.5px] border-blue-500" />
                    )}
                  </span>
                  <span className="flex-1 truncate">{item.label}</span>
                  <span className="flex shrink-0 items-center gap-0.5 text-[10px] text-zinc-400">
                    <Kbd>⇧</Kbd>
                    <Kbd>{item.shortcut}</Kbd>
                  </span>
                </button>
              )
            })}
          </div>
        </>,
        document.body,
      )}
      {commandPaletteOpen && (
        <CommandPalette
          onClose={() => setCommandPaletteOpen(false)}
          selectedUrn={selectedUrn}
          selectedUrns={selectedUrns}
          activeFilter={activeFilter}
          onNavigateToFilter={(filter) => {
            // Same as in OrganizeModal — navigate once; that URL is
            // already an inbox URL. Avoids a stacked history entry.
            navigateToFilter(filter)
          }}
          onArchive={() => {
            if (selectedUrns.size > 0) {
              activeFilter === 'ARCHIVE' ? handleBatchUnarchive(selectedUrns) : handleBatchArchive(selectedUrns)
            } else if (selectedUrn) {
              activeFilter === 'ARCHIVE' ? handleUnarchive(selectedUrn) : handleArchive(selectedUrn)
            }
          }}
          onMarkRead={() => {
            if (selectedUrns.size > 0) handleBatchMarkRead(selectedUrns)
            else if (selectedUrn) {
              const conv = allConversations.find((c) => c.conversationUrn === selectedUrn)
              if (conv) {
                const newRead = !conv.isRead
                setAllConversations((prev) => prev.map((c) =>
                  c.conversationUrn === selectedUrn ? { ...c, isRead: newRead, unreadCount: newRead ? 0 : 1 } : c
                ))
                sendCommand(newRead ? 'MARK_READ' : 'MARK_UNREAD', { conversationUrn: selectedUrn })
              }
            }
          }}
          onStar={() => {
            if (selectedUrns.size > 0) handleBatchStar(selectedUrns)
            else if (selectedUrn) voidAsync(toggleBookmark(selectedUrn), 'toggleBookmark (action-bar)', { urn: selectedUrn })
          }}
          onTag={() => setShowTagPicker(true)}
          onRemind={() => { if (selectedUrn) setShowReminderPicker(true) }}
          onCompose={() => setComposeMode(true)}
          onCopyConversation={() => threadCopyRef.current?.()}
          onOrganize={() => setOrganizeOpen(true)}
          onTemplates={() => setActivePage('templates')}
          onShowShortcuts={() => setShortcutsHelpOpen(true)}
          // Contextual content (kondo-parity 1.7).
          tags={tagsHook.tags}
          getTagsForConversation={tagAssignmentsHook.getTagsForConversation}
          onAssignTag={tagAssignmentsHook.assignTag}
          onRemoveTagAssignment={tagAssignmentsHook.removeTagAssignment}
          composites={compositesHook.composites}
        />
      )}
    </div>
  )
}

function getFilterLabel(filter: string, tags?: { id: string; name: string }[]): string {
  if (filter === 'STARRED') return 'Starred'
  if (filter === 'INVITES') return 'Invitations'
  if (filter === 'CONNECTIONS') return 'Connections'
  if (filter === 'SPONSORED') return 'Sponsored'
  if (filter === 'SPAM') return 'Spam'
  if (filter === 'SALESNAV') return 'Sales Navigator'
  if (filter === 'SALESNAV_ARCHIVE') return 'Sales Navigator · Archive'
  if (filter === 'RECRUITER') return 'Recruiter'
  if (filter === 'RECRUITER_ARCHIVE') return 'Recruiter · Archive'
  if (filter.startsWith('category:')) return 'Category'
  if (filter.startsWith('tag:')) {
    const tagId = filter.slice(4)
    return tags?.find((t) => t.id === tagId)?.name || 'Tag'
  }
  return filter.charAt(0) + filter.slice(1).toLowerCase()
}
